<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-safety-hazard-closed-loop">
    <nx-report-header
      title="隐患上报闭环管理看板"
    >
      <div class="kb-header-actions">
        <el-button size="small" icon="el-icon-warning-outline" @click="goAlarmBoard">实时报警看板</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="cl-pipeline">
      <div
        v-for="s in pipelineSteps"
        :key="s.key"
        class="cl-pipe-card"
        :class="{ 'is-warn': s.variant === 'warn', 'is-danger': s.variant === 'danger' }"
      >
        <div class="cl-pipe-step">{{ s.title }}</div>
        <div class="cl-pipe-val">{{ s.count }}</div>
      </div>
    </div>

    <div class="cl-kpi-row">
      <div class="cl-kpi-card">
        <div class="cl-kpi-label">在办隐患（未关闭未取消）</div>
        <div class="cl-kpi-value">{{ kpi.openTotal }}</div>
      </div>
      <div class="cl-kpi-card">
        <div class="cl-kpi-label">闭环率（已关闭 / 有效单）</div>
        <div class="cl-kpi-value" :class="{ 'is-good': kpi.closeRate >= 70 }">{{ kpi.closeRate }}%</div>
      </div>
      <div class="cl-kpi-card">
        <div class="cl-kpi-label">平均闭环用时（天）</div>
        <div class="cl-kpi-value" :class="{ 'is-warn': kpi.avgDays > 10 }">{{ kpi.avgDays }}</div>
      </div>
      <div class="cl-kpi-card">
        <div class="cl-kpi-label">超期未闭</div>
        <div class="cl-kpi-value" :class="{ 'is-warn': kpi.overdue > 0 }">{{ kpi.overdue }}</div>
      </div>
    </div>

    <div class="cl-charts-row">
      <div class="cl-chart-card">
        <div class="cl-chart-head">
          <span>闭环各阶段件数</span>
        </div>
        <div ref="stageChart" class="cl-chart-box" />
      </div>
      <div class="cl-chart-card">
        <div class="cl-chart-head">
          <span>责任部门 — 在办 vs 已关闭</span>
        </div>
        <div ref="deptChart" class="cl-chart-box" />
      </div>
    </div>

    <div class="cl-section">
      <div class="cl-section-head">
        <span>闭环明细</span>
        <span class="cl-loop-tag">
          <i class="el-icon-s-order" />
          共 {{ rawList.length }} 条
        </span>
      </div>
      <el-table :data="pagedTable" border stripe size="small" max-height="520" style="width: 100%">
        <el-table-column prop="hazardCode" label="隐患编号" width="160" show-overflow-tooltip fixed />
        <el-table-column prop="createTime" label="上报时间" width="136" :formatter="fmtTime" />
        <el-table-column prop="createName" label="上报人" width="88" show-overflow-tooltip />
        <el-table-column prop="responsibleDeptName" label="责任部门" min-width="120" show-overflow-tooltip />
        <el-table-column prop="rectifyPersonName" label="整改负责人" width="100" show-overflow-tooltip />
        <el-table-column prop="requiredCompletionDate" label="要求完成" width="108" :formatter="fmtDate" />
        <el-table-column prop="rectifyStatus" label="整改状态" width="92">
          <template slot-scope="{ row }">
            <el-tag :type="tagRectify(row.rectifyStatus)" size="mini">{{ rectifyText(row.rectifyStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="rectifyCompletionDate" label="整改完成" width="136" :formatter="fmtTime" />
        <el-table-column prop="acceptanceResult" label="验收" width="96">
          <template slot-scope="{ row }">
            <span>{{ acceptanceText(row.acceptanceResult) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="acceptanceDate" label="验收时间" width="136" :formatter="fmtTime" />
        <el-table-column prop="acceptancePersonName" label="验收人" width="88" show-overflow-tooltip />
        <el-table-column prop="remark" label="整改备注" min-width="140" show-overflow-tooltip />
        <el-table-column prop="updateTime" label="最近更新" width="136" :formatter="fmtTime" />
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="rawList.length > pageSize"
        style="margin-top: 12px; text-align: right"
        :current-page.sync="page"
        :page-size="pageSize"
        layout="total, prev, pager, next"
        :total="rawList.length"
      />
    </div>

    <el-dialog
      :title="detailTitle"
      :visible.sync="detailVisible"
      width="760px"
      append-to-body
      @closed="detailRow = {}"
    >
      <el-descriptions v-if="detailVisible" :column="2" border size="small" class="cl-detail-desc">
        <el-descriptions-item label="隐患编号">{{ detailText(detailRow.hazardCode) }}</el-descriptions-item>
        <el-descriptions-item label="问题类型">{{ detailText(detailRow.problemTypeName) }}</el-descriptions-item>
        <el-descriptions-item label="上报人">{{ detailText(detailRow.createName) }}</el-descriptions-item>
        <el-descriptions-item label="上报时间">{{ detailDateTime(detailRow.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="发现时间">{{ detailDateTime(detailRow.discoveryDate) }}</el-descriptions-item>
        <el-descriptions-item label="责任部门">{{ detailText(detailRow.responsibleDeptName) }}</el-descriptions-item>
        <el-descriptions-item label="整改负责人">{{ detailText(detailRow.rectifyPersonName) }}</el-descriptions-item>
        <el-descriptions-item label="要求完成">{{ detailDate(detailRow.requiredCompletionDate) }}</el-descriptions-item>
        <el-descriptions-item label="整改状态">
          <el-tag :type="tagRectify(detailRow.rectifyStatus)" size="mini">{{ rectifyText(detailRow.rectifyStatus) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="整改完成">{{ detailDateTime(detailRow.rectifyCompletionDate) }}</el-descriptions-item>
        <el-descriptions-item label="验收结果">{{ acceptanceText(detailRow.acceptanceResult) }}</el-descriptions-item>
        <el-descriptions-item label="验收人">{{ detailText(detailRow.acceptancePersonName) }}</el-descriptions-item>
        <el-descriptions-item label="验收时间">{{ detailDateTime(detailRow.acceptanceDate) }}</el-descriptions-item>
        <el-descriptions-item label="最近更新">{{ detailDateTime(detailRow.updateTime) }}</el-descriptions-item>
        <el-descriptions-item label="问题描述" :span="2">
          <div class="cl-detail-text">{{ detailText(detailRow.problemDescription) }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="整改备注" :span="2">
          <div class="cl-detail-text">{{ detailText(detailRow.remark) }}</div>
        </el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchSafetyHazardClosedLoopBoard } from './api'
import { RECTIFY_STATUS_LABEL, ACCEPTANCE_LABEL } from '../utils/mockSafetyHazardRectify'

function isWaitAccept(r) {
  return r.rectifyStatus === 2 && (r.acceptanceResult === 0 || r.acceptanceResult == null)
}

function isWaitClose(r) {
  return r.rectifyStatus === 2 && (r.acceptanceResult === 1 || r.acceptanceResult === 3)
}

export default {
  name: 'KanbanSafetyHazardClosedLoopBoard',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      rawList: [],
      kpi: { openTotal: 0, closeRate: 0, avgDays: 0, overdue: 0 },
      chartInstances: [],
      page: 1,
      pageSize: 10,
      detailVisible: false,
      detailRow: {}
    }
  },
  computed: {
    detailTitle() {
      return this.detailRow && this.detailRow.hazardCode
        ? `闭环明细详情 - ${this.detailRow.hazardCode}`
        : '闭环明细详情'
    },
    pipelineSteps() {
      const list = this.rawList
      const now = Date.now()
      const pending = list.filter((r) => r.rectifyStatus === 0).length
      const doing = list.filter((r) => r.rectifyStatus === 1).length
      const waitAcc = list.filter(isWaitAccept).length
      const waitClose = list.filter(isWaitClose).length
      const closed = list.filter((r) => r.rectifyStatus === 3).length
      const cancelled = list.filter((r) => r.rectifyStatus === 4).length
      const overdue = list.filter((r) => {
        const req = r.requiredCompletionDate
        const open = r.rectifyStatus === 0 || r.rectifyStatus === 1
        return req && open && req < now
      }).length
      return [
        { key: 'p0', title: '待整改', count: pending, hint: 'rectify_status=0', variant: pending ? 'warn' : '' },
        { key: 'p1', title: '整改中', count: doing, hint: 'rectify_status=1', variant: '' },
        { key: 'p2', title: '待验收', count: waitAcc, hint: '已整改+待验收', variant: waitAcc ? 'warn' : '' },
        { key: 'p3', title: '待关闭', count: waitClose, hint: '验过待归档', variant: '' },
        { key: 'p4', title: '已关闭', count: closed, hint: 'rectify_status=3', variant: '' },
        { key: 'p5', title: '超期督办', count: overdue, hint: '在办且过要求完成日', variant: overdue ? 'danger' : '' }
      ]
    },
    pagedTable() {
      const start = (this.page - 1) * this.pageSize
      return this.rawList.slice(start, start + this.pageSize)
    }
  },
  watch: {
    rawList: {
      handler() {
        this.page = 1
        this.buildKpi()
        this.$nextTick(() => this.renderCharts())
      }
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    rectifyText(s) {
      if (s == null || s === '') return '—'
      return RECTIFY_STATUS_LABEL[s] != null ? RECTIFY_STATUS_LABEL[s] : String(s)
    },
    acceptanceText(s) {
      if (s == null || s === '') return '—'
      return ACCEPTANCE_LABEL[s] != null ? ACCEPTANCE_LABEL[s] : String(s)
    },
    tagRectify(s) {
      if (s === 0) return 'danger'
      if (s === 1) return 'warning'
      if (s === 2) return 'primary'
      if (s === 3) return 'success'
      return 'info'
    },
    fmtTime(row, col, val) {
      return val != null ? moment(val).format('MM-DD HH:mm') : '—'
    },
    fmtDate(row, col, val) {
      return val != null ? moment(val).format('YYYY-MM-DD') : '—'
    },
    detailText(val) {
      return val != null && val !== '' ? val : '—'
    },
    detailDateTime(val) {
      return val != null ? moment(val).format('YYYY-MM-DD HH:mm:ss') : '—'
    },
    detailDate(val) {
      return val != null ? moment(val).format('YYYY-MM-DD') : '—'
    },
    openDetail(row) {
      this.detailRow = row ? { ...row } : {}
      this.detailVisible = true
    },
    goAlarmBoard() {
      this.$router.push('/kanban-management/safety-hazard-alarm-board')
    },
    async loadData() {
      this.loading = true
      try {
        const res = await fetchSafetyHazardClosedLoopBoard()
        this.rawList = (res && res.list) || []
        this.buildKpi()
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildKpi() {
      const list = this.rawList
      const now = Date.now()
      const openTotal = list.filter((r) => r.rectifyStatus !== 3 && r.rectifyStatus !== 4).length
      const effective = list.filter((r) => r.rectifyStatus !== 4).length
      const closed = list.filter((r) => r.rectifyStatus === 3).length
      const closeRate = effective > 0 ? Math.round((closed / effective) * 1000) / 10 : 0
      const doneSamples = list.filter((r) => r.rectifyCompletionDate != null && r.createTime != null)
      let avgDays = 0
      if (doneSamples.length) {
        const sum = doneSamples.reduce((acc, r) => {
          const days = (r.rectifyCompletionDate - r.createTime) / 86400000
          return acc + (Number.isFinite(days) ? days : 0)
        }, 0)
        avgDays = Math.round((sum / doneSamples.length) * 10) / 10
      }
      const overdue = list.filter((r) => {
        const req = r.requiredCompletionDate
        const open = r.rectifyStatus === 0 || r.rectifyStatus === 1
        return req && open && req < now
      }).length
      this.kpi = { openTotal, closeRate, avgDays, overdue }
    },
    disposeCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    renderCharts() {
      this.disposeCharts()
      const list = this.rawList
      const pending = list.filter((r) => r.rectifyStatus === 0).length
      const doing = list.filter((r) => r.rectifyStatus === 1).length
      const waitAcc = list.filter(isWaitAccept).length
      const waitClose = list.filter(isWaitClose).length
      const closed = list.filter((r) => r.rectifyStatus === 3).length

      const stages = ['待整改', '整改中', '待验收', '待关闭', '已关闭']
      const stageVals = [pending, doing, waitAcc, waitClose, closed]
      const colors = ['#ef4444', '#f59e0b', '#8b5cf6', '#0ea5e9', '#10b981']

      const f = echarts.init(this.$refs.stageChart)
      f.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
        xAxis: {
          type: 'category',
          data: stages,
          axisLabel: { color: '#64748b', fontSize: 11, interval: 0, rotate: 18 }
        },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#64748b' } },
        series: [
          {
            type: 'bar',
            data: stageVals.map((v, i) => ({ value: v, itemStyle: { color: colors[i], borderRadius: [6, 6, 0, 0] } })),
            barMaxWidth: 48
          }
        ]
      })
      this.chartInstances.push(f)

      const deptMap = {}
      list.forEach((r) => {
        const d = r.responsibleDeptName || '未填部门'
        if (!deptMap[d]) deptMap[d] = { open: 0, closed: 0 }
        if (r.rectifyStatus === 3) deptMap[d].closed++
        else if (r.rectifyStatus !== 4) deptMap[d].open++
      })
      const depts = Object.keys(deptMap).slice(0, 10)
      const openArr = depts.map((d) => deptMap[d].open)
      const closedArr = depts.map((d) => deptMap[d].closed)

      const b = echarts.init(this.$refs.deptChart)
      b.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: '3%', right: '4%', bottom: '14%', top: '8%', containLabel: true },
        xAxis: { type: 'value', axisLabel: { color: '#64748b' } },
        yAxis: {
          type: 'category',
          data: depts,
          axisLabel: { color: '#64748b', fontSize: 11 },
          inverse: true
        },
        series: [
          { name: '在办', type: 'bar', stack: 't', data: openArr, itemStyle: { color: '#f59e0b' } },
          { name: '已关闭', type: 'bar', stack: 't', data: closedArr, itemStyle: { color: '#10b981' } }
        ]
      })
      this.chartInstances.push(b)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.kb-header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
